/**
 * Firestore Hooks
 * 
 * React hooks for subscribing to Firestore data with real-time updates.
 * All hooks support project-scoped collection paths via the projectId parameter.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore'
import { db } from '../firebase'
import {
  addDocument,
  updateDocument,
  deleteDocument,
  saveContentDocument,
  getCollectionPath,
  COLLECTIONS,
  CONTENT_DOCS,
} from '../services/firestore'

/**
 * Subscribe to a collection with real-time updates
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection to subscribe to
 * @param {object} options - Query options (where, orderBy, limit)
 * @returns {object} - { data, loading, error }
 */
export function useCollection(projectId, collectionName, options = {}) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    
    const collectionPath = getCollectionPath(projectId, collectionName)
    let q = collection(db, collectionPath)
    const constraints = []
    
    if (options.whereClause) {
      constraints.push(
        where(options.whereClause.field, options.whereClause.op, options.whereClause.value)
      )
    }
    
    if (options.orderByField) {
      constraints.push(orderBy(options.orderByField, options.orderDirection || 'asc'))
    }
    
    if (options.limitCount) {
      constraints.push(limit(options.limitCount))
    }
    
    if (constraints.length > 0) {
      q = query(q, ...constraints)
    }
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
        setData(docs)
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('Firestore subscription error:', err)
        setError(err)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [projectId, collectionName, JSON.stringify(options)])

  return { data, loading, error }
}

/**
 * Subscribe to a single document with real-time updates
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection name
 * @param {string} docId - The document ID
 * @returns {object} - { data, loading, error }
 */
export function useDocument(projectId, collectionName, docId) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!docId) {
      setData(null)
      setLoading(false)
      return
    }

    setLoading(true)
    
    const collectionPath = getCollectionPath(projectId, collectionName)
    const docRef = doc(db, collectionPath, docId)
    
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setData({ id: snapshot.id, ...snapshot.data() })
        } else {
          setData(null)
        }
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('Firestore document subscription error:', err)
        setError(err)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [projectId, collectionName, docId])

  return { data, loading, error }
}

/**
 * Hook providing CRUD operations for a collection
 * @param {string|null} projectId - The project ID (null for legacy flat collections)
 * @param {string} collectionName - The collection name
 * @returns {object} - { add, update, remove, loading, error }
 */
export function useFirestoreOperations(projectId, collectionName) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const add = useCallback(async (data) => {
    setLoading(true)
    setError(null)
    try {
      const id = await addDocument(projectId, collectionName, data)
      setLoading(false)
      return id
    } catch (err) {
      setError(err)
      setLoading(false)
      throw err
    }
  }, [projectId, collectionName])

  const update = useCallback(async (docId, data) => {
    setLoading(true)
    setError(null)
    try {
      await updateDocument(projectId, collectionName, docId, data)
      setLoading(false)
    } catch (err) {
      setError(err)
      setLoading(false)
      throw err
    }
  }, [projectId, collectionName])

  const remove = useCallback(async (docId) => {
    setLoading(true)
    setError(null)
    try {
      await deleteDocument(projectId, collectionName, docId)
      setLoading(false)
    } catch (err) {
      setError(err)
      setLoading(false)
      throw err
    }
  }, [projectId, collectionName])

  return { add, update, remove, loading, error }
}

// Convenience hooks for specific collections

export function useCharacters(projectId, options) {
  return useCollection(projectId, COLLECTIONS.CHARACTERS, options)
}

export function useCharacter(projectId, id) {
  return useDocument(projectId, COLLECTIONS.CHARACTERS, id)
}

export function useCharacterOperations(projectId) {
  return useFirestoreOperations(projectId, COLLECTIONS.CHARACTERS)
}

export function useLocations(projectId, options) {
  return useCollection(projectId, COLLECTIONS.LOCATIONS, options)
}

export function useLocation(projectId, id) {
  return useDocument(projectId, COLLECTIONS.LOCATIONS, id)
}

export function useLocationOperations(projectId) {
  return useFirestoreOperations(projectId, COLLECTIONS.LOCATIONS)
}

export function useEvents(projectId, options) {
  return useCollection(projectId, COLLECTIONS.EVENTS, options)
}

export function useEvent(projectId, id) {
  return useDocument(projectId, COLLECTIONS.EVENTS, id)
}

export function useEventOperations(projectId) {
  return useFirestoreOperations(projectId, COLLECTIONS.EVENTS)
}

export function useShots(projectId, options) {
  return useCollection(projectId, COLLECTIONS.SHOTS, options)
}

export function useShot(projectId, id) {
  return useDocument(projectId, COLLECTIONS.SHOTS, id)
}

export function useShotOperations(projectId) {
  return useFirestoreOperations(projectId, COLLECTIONS.SHOTS)
}

export function useImages(projectId, options) {
  return useCollection(projectId, COLLECTIONS.IMAGES, options)
}

export function useImage(projectId, id) {
  return useDocument(projectId, COLLECTIONS.IMAGES, id)
}

export function useImageOperations(projectId) {
  return useFirestoreOperations(projectId, COLLECTIONS.IMAGES)
}

// Synopsis (singleton content document)

export function useSynopsis(projectId) {
  return useDocument(projectId, COLLECTIONS.CONTENT, CONTENT_DOCS.SYNOPSIS)
}

export function useSynopsisOperations(projectId) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const save = useCallback(async (data) => {
    setLoading(true)
    setError(null)
    try {
      await saveContentDocument(projectId, CONTENT_DOCS.SYNOPSIS, data)
      setLoading(false)
    } catch (err) {
      setError(err)
      setLoading(false)
      throw err
    }
  }, [projectId])

  return { save, loading, error }
}
